import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const TIMEOUT_MS = 60 * 60 * 1000; // 1 hour
const CHECK_INTERVAL_MS = 15 * 1000;
const LAST_ACTIVITY_KEY = "kids_last_activity";
const ACTIVITY_EVENTS = ["mousedown", "mousemove", "keydown", "touchstart", "wheel", "scroll"];

function hasActiveSession() {
  return !!localStorage.getItem("kids_parent_token");
}

function clearKidsSession() {
  localStorage.removeItem("kids_parent_token");
  localStorage.removeItem("kids_active_mode");
  localStorage.removeItem("kids_child");
  localStorage.removeItem(LAST_ACTIVITY_KEY);
  ["childId", "childName", "childGrade", "childXP", "childCoins", "childStreak"].forEach((k) =>
    sessionStorage.removeItem(k)
  );
}

/**
 * Logs the kid/parent session out after 1 hour of inactivity anywhere under
 * /kids/*. Last-activity is persisted to localStorage so the timeout also
 * fires correctly after a full reload or a reopened tab/browser, not just
 * while the app stays mounted. Mount once near the router root.
 */
export default function KidsIdleLogout() {
  const navigate = useNavigate();
  const location = useLocation();
  const intervalRef = useRef(null);

  useEffect(() => {
    const inKidsSection = location.pathname.startsWith("/kids");
    if (!inKidsSection) return;

    const touch = () => {
      if (hasActiveSession()) localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    };

    const checkExpiry = () => {
      if (!hasActiveSession()) return;
      const last = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || Date.now());
      if (Date.now() - last >= TIMEOUT_MS) {
        const wasKid = localStorage.getItem("kids_active_mode") === "child";
        clearKidsSession();
        navigate(wasKid ? "/kids/kid-login" : "/kids/login", { replace: true });
      }
    };

    touch();
    checkExpiry();

    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, touch, { passive: true }));
    document.addEventListener("visibilitychange", checkExpiry);
    intervalRef.current = setInterval(checkExpiry, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, touch));
      document.removeEventListener("visibilitychange", checkExpiry);
      clearInterval(intervalRef.current);
    };
  }, [location.pathname, navigate]);

  return null;
}
